import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Label, Input, Table, Button, Spinner } from "reactstrap";
import axios from "axios";
import Header from "../../../layout/Header/Header";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";

/**
 * Requisicion contra salida de habilitacion, por orden de maquila.
 *
 * De cada orden abierta: lo que pide su explosion contra lo que ya se le mando
 * al maquilero con traspasos ligados a la orden. Arriba avisa cuantas ordenes
 * tienen habilitacion pendiente de mandar; "Mandar" abre el traspaso ya ligado
 * a la orden y prellenado con lo pendiente.
 */
function ReporteSalidaHabilitacion() {
  const { user } = useContext(AuthContext);
  const URL_REQUISICION = process.env.REACT_APP_URL_REQUISICION;

  const [filas, setFilas] = useState([]);
  const [tipo, setTipo] = useState("Habilitacion");
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [abierta, setAbierta] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    let vigente = true; // descarta respuestas de un filtro anterior
    setCargando(true);
    axios
      .get(`${URL_REQUISICION}PendientesEnviar?tipoMaterial=${tipo}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => vigente && setFilas(res.data))
      .catch((err) => console.log(err))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, [tipo]);

  const conPendiente = filas.filter((f) => f.materialesPendientes > 0);
  const sinExplosion = filas.filter((f) => f.sinExplosion);
  const visibles = soloPendientes ? filas.filter((f) => f.materialesPendientes > 0 || f.sinExplosion) : filas;
  const n = (x) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(x || 0);
  const estado = (f) => (f.sinExplosion ? "Sin explosion" : f.completa ? "Completa" : `Faltan ${f.materialesPendientes}`);

  // Un renglon por material pendiente: es lo que sirve para ir a surtirlo.
  const detalle = () =>
    visibles.flatMap((f) =>
      (f.renglones.length ? f.renglones : [{}])
        .filter((r) => !soloPendientes || r.pendiente > 0 || f.sinExplosion)
        .map((r) => [
          f.numero, f.maquilero, f.pedido, f.cliente, f.fechaCompromiso || "",
          r.nombre || (f.sinExplosion ? "SIN EXPLOSION" : ""), r.color || "",
          n(r.requerido), n(r.enviado), n(r.pendiente),
        ])
    );
  const COLUMNAS = ["Orden", "Maquilero", "Pedido", "Cliente", "Compromiso", "Material", "Color", "Requerido", "Mandado", "Pendiente"];

  function PDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 230, 8, 45, 22);
    doc.text(`Requisicion vs salida de ${tipo === "Telas" ? "tela" : "habilitacion"}`, 20, 18);
    doc.setFontSize(10);
    doc.text(`${conPendiente.length} orden(es) con material pendiente de mandar`, 20, 26);
    doc.autoTable({ head: [COLUMNAS], body: detalle(), startY: 34, styles: { fontSize: 8 } });
    doc.save("RequisicionVsSalida.pdf");
  }

  function excel() {
    new ExportJsonExcel({
      fileName: "RequisicionVsSalida",
      datas: [{
        sheetData: detalle().map((r) => Object.fromEntries(COLUMNAS.map((c, i) => [c, r[i]]))),
        sheetName: "Pendientes",
        sheetFilter: COLUMNAS,
        sheetHeader: COLUMNAS,
      }],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_telas || user.menu_produccion ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-tasks" style={{ marginRight: 8, color: "#f59e0b" }} />
                Requisicion vs Salida de Habilitacion
              </h3>
              <p className="page-subtitle">Lo que pide cada orden de maquila contra lo que ya se le mando</p>
            </div>
            <div>
              <Button color="danger" size="sm" onClick={PDF} style={{ marginRight: 6 }}>
                PDF
              </Button>
              <Button color="success" size="sm" onClick={excel}>
                Excel
              </Button>
            </div>
          </div>

          {!cargando && conPendiente.length ? (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "10px 14px", borderRadius: 8, marginBottom: 12 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
              <b>{conPendiente.length}</b> orden(es) de maquila con {tipo === "Telas" ? "tela" : "habilitacion"} pendiente de mandar:{" "}
              {conPendiente.map((f) => f.numero).join(", ")}
            </div>
          ) : undefined}
          {!cargando && sinExplosion.length ? (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", padding: "8px 14px", borderRadius: 8, marginBottom: 12 }}>
              {sinExplosion.length} orden(es) sin explosion capturada, no se puede saber que material piden:{" "}
              {sinExplosion.map((f) => f.numero).join(", ")}
            </div>
          ) : undefined}

          <Row style={{ marginBottom: 12 }}>
            <Col md={3}>
              <Label>Material</Label>
              <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="Habilitacion">Habilitacion</option>
                <option value="Telas">Tela</option>
                <option value="todos">Todo</option>
              </Input>
            </Col>
            <Col md={4} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button size="sm" color={soloPendientes ? "primary" : "secondary"} onClick={() => setSoloPendientes(!soloPendientes)}>
                {soloPendientes ? "Viendo solo con pendiente" : "Viendo todas las ordenes abiertas"}
              </Button>
            </Col>
          </Row>

          {cargando ? (
            <Spinner />
          ) : (
            <Table size="sm" striped borderless responsive>
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Fecha</th>
                  <th>Compromiso</th>
                  <th>Maquilero</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Articulo</th>
                  <th>Estado</th>
                  <th>Detalle</th>
                  <th>Mandar</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((f) => (
                  <React.Fragment key={f._id}>
                    <tr>
                      <td>{f.numero}</td>
                      <td>{f.fecha}</td>
                      <td>{f.fechaCompromiso}</td>
                      <td>{f.maquilero}</td>
                      <td>{f.pedido}</td>
                      <td>{f.cliente}</td>
                      <td>{f.articulo}</td>
                      <td style={{ fontWeight: 600, color: f.completa ? "#16a34a" : f.sinExplosion ? "#b45309" : "#dc2626" }}>
                        {estado(f)}
                      </td>
                      <td>
                        <Button title="Buscar" size="sm" color="info" onClick={() => setAbierta(abierta === f._id ? null : f._id)}>
                          <i className="fas fa-search" />
                        </Button>
                      </td>
                      <td>
                        {f.materialesPendientes > 0 && f.maquilero !== "Sin maquilero" ? (
                          <Button title="Traspaso" size="sm" color="warning" href={`/TraspasosMaterialesCreate?corte=${f._id}`}>
                            <i className="fas fa-exchange-alt" />
                          </Button>
                        ) : f.materialesPendientes > 0 ? (
                          <span className="text-muted" style={{ fontSize: 12 }}>Asigna maquilero</span>
                        ) : undefined}
                      </td>
                    </tr>
                    {abierta === f._id ? (
                      <tr>
                        <td colSpan={10} style={{ background: "#f8fafc" }}>
                          <Table size="sm" borderless style={{ marginBottom: 0 }}>
                            <thead>
                              <tr>
                                <th>Material</th>
                                <th>Color</th>
                                <th>Por prenda</th>
                                <th style={{ textAlign: "right" }}>Requerido</th>
                                <th style={{ textAlign: "right" }}>Mandado</th>
                                <th style={{ textAlign: "right" }}>Pendiente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {f.renglones.map((r) => (
                                <tr key={`${r.material}${r.colores}`}>
                                  <td>{r.nombre}</td>
                                  <td>{r.color}</td>
                                  <td>{r.cantidadPrenda}</td>
                                  <td style={{ textAlign: "right" }}>{n(r.requerido)}</td>
                                  <td style={{ textAlign: "right" }}>{n(r.enviado)}</td>
                                  <td style={{ textAlign: "right", fontWeight: 600, color: r.pendiente > 0 ? "#dc2626" : "#16a34a" }}>
                                    {n(r.pendiente)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    ) : undefined}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteSalidaHabilitacion;
